import { inject, Inject, Injectable } from '@angular/core';
import { Asset } from './asset.dto';
import { COMMONS_ENV, HttpService } from '@damofire/data-angular-commons';
import { createBatch, deleteBatch, ID_FIELD_OPTIONS, Query, SearchResult, updateBatch, WW_ASSETS_COLLECTION } from '@damofire/data-commons';
import { AuthDtoSignerService } from '@damofire/data-security';
import { AssetService, generateAndAssignAssetIds } from './asset.service';
import { doc, Firestore, getDoc, snapToData } from '@angular/fire/firestore';
import { firstValueFrom } from 'rxjs';
import { FirebaseIssueService } from '../issue/firebase-issue.service';
import { FirebaseObservableAssetService } from './firebase-asset-observable.service';

@Injectable()
export class FirebaseAssetService extends AssetService {
  private readonly firestore = inject(Firestore);

  constructor(
    protected readonly issueService: FirebaseIssueService,
    protected readonly http: HttpService,
    protected readonly dtoSigner: AuthDtoSignerService,
    @Inject(COMMONS_ENV) protected readonly environment: any,
    private readonly observableAssetService: FirebaseObservableAssetService,
  ) {
    super(issueService, http, dtoSigner);
  }

  async get(division: string, id: string | number): Promise<Asset> {
    const snap = await getDoc(doc(this.firestore, `${WW_ASSETS_COLLECTION}/${id}`));
    return snapToData<Asset>(snap, ID_FIELD_OPTIONS);
  }

  async search(divisionId: string, sQuery: Query): Promise<SearchResult<Asset>> {
    return firstValueFrom(this.observableAssetService.search(divisionId, sQuery));
  }

  async updateBatch(assets: Asset[]): Promise<void> {
    for (const asset of assets) {
      await this.dtoSigner.sign(asset, false, false);
    }
    return this.executeUpdateBatch(assets);
  }

  protected executeUpdateBatch(assets: Asset[]): Promise<void> {
    return updateBatch(WW_ASSETS_COLLECTION, assets);
  }

  protected async dbUpdate(division: string, updated: Asset): Promise<void> {
    await this.dtoSigner.update(updated, `${WW_ASSETS_COLLECTION}/${updated._id}`);
  }

  async create(divisionId: string, assets: Asset[]): Promise<Asset[]> {
    const assetWithIds: Asset[] = generateAndAssignAssetIds(assets);

    for (const asset of assetWithIds) {
      await this.dtoSigner.sign(asset, true);
    }

    await createBatch(WW_ASSETS_COLLECTION, assetWithIds);
    return assetWithIds;
  }

  async delete(divisionId: string, assets: Asset[]): Promise<void> {
    return deleteBatch(WW_ASSETS_COLLECTION, assets);
  }
}
